import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, Col, Layout, message, Row, Modal, Space, Input, Typography, Divider, Card } from 'antd';
import { CalendarFilled } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { CustomerData } from '../../commerce/components/CustomerData';
import { PageTitle } from '../../components/layouts/page/PageTitle';
import { DateUpdateFlexRow } from '../../components/widgets/DateUpdateFlexRow';
import { addLeadingChar } from '../../lib/addLeadingChar';
import { ItemListCard } from '../components/ItemListCard';
import { OrderPriceCard } from '../components/OrderPriceCard';
import { useOrders as useAdminOrders, useOrderDetail } from '../../hooks/data/useOrders';
import { OrderNotificationCard } from '../components/OrderNotificationCard';
import { Order } from '../../hooks/data/models';

export const OrderDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams() as { id: string };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);

  const { deleteOrder, isDeleteError } = useAdminOrders();
  const { order, lastUpdated, refreshOrderDetail, isLoading, 
    confirmOrder, updateOrder, notifyOrderConfirmation, actionError
   } = useOrderDetail(params.id);
  
  const [draftOrder, setDraftOrder] = useState<Order | undefined >(order);

  //
  useEffect(() => {
    if (order) {
      setDraftOrder(order);
    }
  }, [order]);

  const handleConfirm = () => {
    if(!order)
      return;

    confirmOrder().catch(() => {
      if(actionError)
        message.error(actionError.message);
      else
        message.error("An error occurred during order confirmation")
    });
  };

  const handleReject = () => {};

  //Modal methods
  const showDeleteOrderModal = () => {
    setDeleteConfirmationText("");
    setIsDeleteModalOpen(true);
  };
  const handleDeleteModalCancel = () => {
    setDeleteConfirmationText("");
    setIsDeleteModalOpen(false);
  }
  const handleDeleteConfirmation = async () => {
    if(!order || deleteConfirmationText !== "DELETE")
      return;
    const deleteSuccess = await deleteOrder(order._id);
    if(deleteSuccess){
      setIsDeleteModalOpen(false);
      navigate("/orders");
    }    
  }

  const handleOrderNotification = () => {
    notifyOrderConfirmation().catch((e) => {
      let messageText: string;
      if(axios.isAxiosError(e))
        messageText = e.response?.data.message? e.response.data.message : e.message;
      else
        messageText = e.message;

      message.error("An error occurred requesting the server to send notifications: " + messageText);
    });
  }
  const checkAndSendOrderNotification = () => {
    //Check if notification date alredy exist
    if(!order?.lastConfirmationEmailAt)
      handleOrderNotification();
    else
      setIsNotificationModalOpen(true);
  }

  const handleOrderDataEdited = (updates: any) => {
    setDraftOrder(prev => ({
      ...prev,
      ...updates // Merges all changed fields at once
    }));
  }

  const handleEditOrderCancel = () => {
    setIsEditMode(false);
    setDraftOrder(order);
  }
  const handleEditOrderConfirm = () => {
    if(!draftOrder) return;
    
    updateOrder(draftOrder).catch(() => {
      if(actionError)
        message.error(actionError.message);
      else
        message.error("An error occurred updating the order");
    }).then(() => {
      setIsEditMode(false);
    });
    //When the order is updated from SWR cache, useEffect() updates the draftOrder
    //No need to do anything here
  }

  //Utility methods
  const formatDate = (dateString: string) => {
    
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        }).format(new Date(dateString));
  };

  const orderNumber = order?.number ? addLeadingChar(order.number.toString(), '0', 4) : '';
  
  const { Text } = Typography;

  return (
    <Layout.Content>
  
      <PageTitle 
        onBack={() => navigate(-1)} 
        title={'Order #' + orderNumber} 
        actions = {
          <Space>
            <Button onClick={() => showDeleteOrderModal()} title={'Delete order'} disabled={isEditMode} danger >
                Delete
            </Button>
            {!isEditMode && <Button onClick={() => {setIsEditMode(true);}} title={'Edit order'} disabled={order?.paymentStatus === "fullyPaid"}>
                Edit
            </Button>}
            { isEditMode && <Button onClick={handleEditOrderCancel} title={'Cancel edit'}>
                Cancel Edit
            </Button>}
            { isEditMode && <Button onClick={handleEditOrderConfirm} type="primary" title={'Save order'}>
                Save Order
            </Button> }
            <Divider type='vertical'></Divider>
            <Button type={'primary'} onClick={handleReject} key="reject-button" disabled>
              Reject
            </Button>
            <Button type={'primary'} onClick={handleConfirm} disabled={isEditMode || order?.paymentStatus === "fullyPaid"} key="confirm-button">
              Confirm
            </Button>
          </Space>} 
      />

      <DateUpdateFlexRow 
        lastUpdated={lastUpdated}
        refreshCallback={refreshOrderDetail}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Space style={{marginBottom: '2rem'}}>
            <CalendarFilled style={{ fontSize: '2rem', color: '#96a9c8' }} />
            {order && <OrderDate>Received on: {formatDate(order.createdAt)}</OrderDate>}
          </Space>
        </Col>

        <Col xs={24} lg={16} xxl={16}>
          {isEditMode && (
            <Card title="Order number" style={{marginBottom: '2rem'}}>
              <Input value={draftOrder?.number} onChange={(e) => {
                  let val: number = Number(e.target.value);
                  handleOrderDataEdited({number: val})
                }} type='number'></Input>
            </Card>
          )}

          <ItemListCard 
            order={isEditMode ? draftOrder : order} 
            loading={isLoading} 
            isEditMode={isEditMode} 
            onDataEdited={handleOrderDataEdited}/>

          <OrderPriceCard 
            order={isEditMode ? draftOrder : order} 
            loading={isLoading} 
            isEditMode={isEditMode} 
            onDataEdited={handleOrderDataEdited}/>       
        </Col>

        <Col xs={24} lg={8} xxl={8}>
          <OrderNotificationCard orderId={order?._id} 
            orderNotificationDate={order?.lastConfirmationEmailAt}
            isEditMode={isEditMode}
            requestHandler={checkAndSendOrderNotification} />
          
          <CustomerData
            customer={isEditMode ? draftOrder?.customer : order?.customer}
            shippingAddress={isEditMode ? draftOrder?.shippingAddress : order?.shippingAddress}
            billingAddress={isEditMode ? draftOrder?.billingAddress : order?.billingAddress}
            shippingAddressTitle='Order Shipping Address'
            billingAddressTitle='Order Billing Address'
            isEditMode={isEditMode}
            onDataEdited={handleOrderDataEdited}
            margin /> 
        </Col>     
      </Row>

      <Modal title="Delete Order" open={isDeleteModalOpen} okType={'danger'} onOk={handleDeleteConfirmation} onCancel={handleDeleteModalCancel}>
          <p>Do you want to permanently delete this order?</p>
          <Input placeholder="Type DELETE and confirm" value={deleteConfirmationText} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setDeleteConfirmationText(e.target.value);}}/>
          {isDeleteError && <Text type="danger">{isDeleteError?.message}</Text>}
      </Modal>

      <Modal title="Send confirmation email" 
        open={isNotificationModalOpen} 
        onOk={() => {
          handleOrderNotification();
          setIsNotificationModalOpen(false);
        }} 
        onCancel={() => setIsNotificationModalOpen(false)}>
          <p>A confirmation email has already been set. Do you want to send a new email?</p>
      </Modal>

    </Layout.Content>
  );
};

const OrderDate = styled.span`
  font-family: Quicksand, 'sans-serif';
  color: #3e475e;
  font-weight: 500;
  margin-left: 0.8rem;
  font-size: 1.6rem;
`;
